// C2 — Importar programación semanal de líderes desde Excel.
//
// Plantilla plana: Fecha · Línea (código) · Turno (número) · Líder (email).
// El parseo + mapeo viven en el FE (SheetJS); cada fila válida se crea vía el
// endpoint single existente (POST /programacion-lideres), que revalida scope,
// fecha y conflictos del lado servidor. El import es ADITIVO: las celdas ya
// ocupadas se marcan como error (no se sobreescriben — el coordinador quita
// primero, igual que en el drag & drop).
//
// Derivación:
//   - Línea: por código (o nombre) contra data.lineas (ya scopeadas).
//   - Turno: por número contra data.turnos.
//   - Líder: por email contra data.lideres_enriquecidos.

import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import type { CoordinadorProgramacionSemana } from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useCrearProgramacionLider } from '../../lib/api/queries';
import { ApiError } from '../../lib/api/client';
import { parseFechaISO } from './excelFecha';

type Data = CoordinadorProgramacionSemana;

interface FilaParseada {
  idx: number;
  filaExcel: number;
  fecha: string;
  lineaTexto: string;
  turnoTexto: string;
  liderTexto: string;
  linea_id: string | null;
  turno_id: string | null;
  lider_user_id: string | null;
  error: string | null;
}

const norm = (s: string) => s.trim().toLowerCase();

export function C2ImportarLideresDialog({ data, onClose }: { data: Data; onClose: () => void }) {
  const [filas, setFilas] = useState<FilaParseada[]>([]);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const crear = useCrearProgramacionLider();
  const [cargando, setCargando] = useState(false);

  const lineaPorCodigo = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of data.lineas) {
      m.set(norm(l.codigo), l.id);
      m.set(norm(l.nombre), l.id); // tolerante: también por nombre
    }
    return m;
  }, [data.lineas]);
  const turnoPorNumero = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of data.turnos) m.set(t.numero, t.id);
    return m;
  }, [data.turnos]);
  const liderPorEmail = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of data.lideres_enriquecidos) m.set(norm(l.email), l.id);
    return m;
  }, [data.lideres_enriquecidos]);

  const lineaLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of data.lineas) m.set(l.id, `${l.codigo} · ${l.nombre}`);
    return m;
  }, [data.lineas]);
  const liderLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of data.lideres_enriquecidos) m.set(l.id, `${l.nombre} ${l.apellido}`);
    return m;
  }, [data.lideres_enriquecidos]);

  // Celdas (fecha|turno|linea) ya ocupadas en la BD para esta semana.
  const ocupadas = useMemo(() => {
    const s = new Set<string>();
    for (const p of data.programaciones) s.add(`${p.fecha}|${p.turno_id}|${p.linea_id}`);
    return s;
  }, [data.programaciones]);

  function descargarPlantilla() {
    const wb = XLSX.utils.book_new();
    const header = ['Fecha (YYYY-MM-DD)', 'Línea (código)', 'Turno (1/2/3)', 'Líder (email)'];
    const ejemplo = [
      data.dias[0],
      data.lineas[0]?.codigo ?? '',
      data.turnos[0]?.numero ?? 1,
      data.lideres_enriquecidos[0]?.email ?? '',
    ];
    const ws = XLSX.utils.aoa_to_sheet([header, ejemplo]);
    XLSX.utils.book_append_sheet(wb, ws, 'Lideres');

    const refLineas = [
      ['LÍNEAS', ''],
      ['Código', 'Nombre'],
      ...data.lineas.map((l) => [l.codigo, l.nombre]),
      ['', ''],
      ['TURNOS', ''],
      ['Número', 'Horario'],
      ...[...data.turnos]
        .sort((a, b) => a.numero - b.numero)
        .map((t) => [t.numero, `${t.hora_inicio.slice(0, 5)}–${t.hora_fin.slice(0, 5)}`]),
      ['', ''],
      ['LÍDERES', ''],
      ['Nombre', 'Email'],
      ...data.lideres_enriquecidos.map((l) => [`${l.nombre} ${l.apellido}`, l.email]),
    ];
    const wsRef = XLSX.utils.aoa_to_sheet(refLineas);
    XLSX.utils.book_append_sheet(wb, wsRef, 'Referencia');
    XLSX.writeFile(wb, `programacion-lideres-${data.semana_iso}.xlsx`);
  }

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreArchivo(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const ws = wb.Sheets['Lideres'] ?? wb.Sheets[wb.SheetNames[0]!];
        const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws!, { header: 1, raw: false, defval: '' });
        // Segunda pasada cruda: la fecha viene como serial de Excel; la
        // formateamos con parseFechaISO (las demás columnas usan `aoa`).
        const aoaRaw = XLSX.utils.sheet_to_json<unknown[]>(ws!, {
          header: 1,
          raw: true,
          defval: '',
        });
        const diasSet = new Set(data.dias);
        const vistasCelda = new Set<string>(); // dedupe dentro del archivo
        const out: FilaParseada[] = [];
        for (let i = 1; i < aoa.length; i++) {
          const r = aoa[i]!;
          const fecha = parseFechaISO(aoaRaw[i]?.[0] ?? r[0]);
          const lineaTexto = String(r[1] ?? '').trim();
          const turnoTexto = String(r[2] ?? '').trim();
          const liderTexto = String(r[3] ?? '').trim();
          if (!fecha && !lineaTexto && !turnoTexto && !liderTexto) continue;

          const linea_id = lineaPorCodigo.get(norm(lineaTexto)) ?? null;
          const turnoNum = Number(turnoTexto.replace(/[^0-9]/g, ''));
          const turno_id = Number.isFinite(turnoNum)
            ? (turnoPorNumero.get(turnoNum) ?? null)
            : null;
          const lider_user_id = liderPorEmail.get(norm(liderTexto)) ?? null;

          let error: string | null = null;
          if (!diasSet.has(fecha)) error = `Fecha "${fecha}" fuera de la semana`;
          else if (!linea_id) error = `Línea "${lineaTexto}" no existe en su scope`;
          else if (!turno_id) error = `Turno "${turnoTexto}" no válido`;
          else if (!lider_user_id) error = `Líder "${liderTexto}" no encontrado`;
          else {
            const celdaKey = `${fecha}|${turno_id}|${linea_id}`;
            if (ocupadas.has(celdaKey)) error = 'Celda ya ocupada (quite el líder actual primero)';
            else if (vistasCelda.has(celdaKey)) error = 'Fila duplicada en el archivo';
            // Doble asignación (mismo líder en otra línea ese fecha/turno) NO es
            // error: el spec del Líder la permite para las líneas auxiliares.
            if (!error) vistasCelda.add(celdaKey);
          }

          out.push({
            idx: out.length,
            filaExcel: i + 1,
            fecha,
            lineaTexto,
            turnoTexto,
            liderTexto,
            linea_id,
            turno_id,
            lider_user_id,
            error,
          });
        }
        setFilas(out);
        if (out.length === 0) toast.error('El archivo no tiene filas de datos');
      } catch {
        toast.error('No se pudo leer el archivo. ¿Es un .xlsx válido?');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const cargables = filas.filter((f) => !f.error);
  const errores = filas.filter((f) => f.error);
  const puedeCargar = cargables.length > 0 && !cargando;

  async function cargar() {
    if (!puedeCargar) return;
    setCargando(true);
    let ok = 0;
    const fails: string[] = [];
    for (const f of cargables) {
      try {
        await crear.mutateAsync({
          fecha: f.fecha,
          turno_id: f.turno_id!,
          linea_id: f.linea_id!,
          lider_user_id: f.lider_user_id!,
          es_linea_principal: true,
        });
        ok++;
      } catch (err) {
        fails.push(`Fila ${f.filaExcel}: ${err instanceof ApiError ? err.message : 'error'}`);
      }
    }
    setCargando(false);
    if (ok > 0) toast.success(`${ok} asignación(es) de líder cargadas`);
    if (fails.length > 0) {
      toast.error(`${fails.length} fila(s) no se cargaron`, {
        description: fails.slice(0, 3).join(' · '),
      });
    } else {
      onClose();
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-4xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar líderes desde Excel · {data.semana_iso}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" type="button" onClick={descargarPlantilla}>
              Descargar plantilla
            </Button>
            <Button variant="outline" type="button" onClick={() => inputRef.current?.click()}>
              Subir archivo
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={onArchivo}
            />
            {nombreArchivo ? (
              <span className="text-xs text-fg-tertiary">{nombreArchivo}</span>
            ) : null}
          </div>

          {filas.length > 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm">
                {cargables.length} lista(s)
                {errores.length > 0 ? (
                  <span className="text-crit"> · {errores.length} con error</span>
                ) : null}
              </p>
              <div className="max-h-80 overflow-auto rounded border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-surface-overlay text-fg-tertiary">
                    <tr>
                      <th className="px-2 py-1 text-left">Fila</th>
                      <th className="px-2 py-1 text-left">Fecha</th>
                      <th className="px-2 py-1 text-left">Línea</th>
                      <th className="px-2 py-1 text-left">Turno</th>
                      <th className="px-2 py-1 text-left">Líder</th>
                      <th className="px-2 py-1 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f) => (
                      <tr key={f.idx} className="border-t border-border">
                        <td className="px-2 py-1 align-top text-fg-tertiary">{f.filaExcel}</td>
                        <td className="px-2 py-1 align-top">{f.fecha}</td>
                        <td className="px-2 py-1 align-top">
                          {f.linea_id ? lineaLabel.get(f.linea_id) : f.lineaTexto}
                        </td>
                        <td className="px-2 py-1 align-top">{f.turnoTexto}</td>
                        <td className="px-2 py-1 align-top">
                          {f.lider_user_id ? liderLabel.get(f.lider_user_id) : f.liderTexto}
                        </td>
                        <td className="px-2 py-1 align-top">
                          {f.error ? (
                            <span className="text-crit">{f.error}</span>
                          ) : (
                            <span className="text-ok">Lista</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-fg-tertiary">
              Descargue la plantilla (Fecha · Línea · Turno · Líder), complétela y súbala. El
              sistema deriva la línea por código, el turno por número y el líder por email. Las
              celdas ya ocupadas se marcan como error (no se sobreescriben). El import es aditivo.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={cargar} disabled={!puedeCargar}>
            {cargando ? 'Cargando…' : `Cargar ${cargables.length} fila(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
